<template>
  <div class="login-buttons">
    <template v-if="currentUser.loggedIn">
      <div v-if="showPatientLoginMenu" class="login-cta-dropdown">
        <button
          type="button"
          class="button-login login-cta-dropdown__trigger"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          aria-haspopup="true"
          aria-controls="login-cta-dropdown-panel"
          @click.stop="toggleMenu"
        >
          <span class="login-cta-dropdown__name">{{ displayName }}</span>
          <i
            class="fa fa-chevron-down login-cta-dropdown__caret"
            :class="{ 'login-cta-dropdown__caret--open': menuOpen }"
            aria-hidden="true"
          />
        </button>
        <div
          v-show="menuOpen"
          id="login-cta-dropdown-panel"
          class="login-cta-dropdown__panel"
          role="menu"
          @click.stop
        >
          <a href="/dashboard" class="login-cta-dropdown__link" role="menuitem">Dashboard</a>
          <a href="/dashboard/logout" class="login-cta-dropdown__link" role="menuitem">Log out</a>
        </div>
      </div>
      <div v-else class="flex flex-row gap-2 login-cta-staff" style="height: 100%;">
        <a
          v-show="currentUser.firstName && !isDashboard"
          href="/dashboard"
          class="button button-login button-login--dashboard"
          style="height: 100%;"
          title="Dashboard"
        >
          <i class="fa fa-th-large dashboard-icon" aria-hidden="true"></i>
          <span class="dashboard-text">Dashboard</span>
        </a>
        <a
          href="/dashboard/logout"
          class="flex items-center justify-center button button-login button-login--logout"
          :class="{ 'lg:hidden': !isDashboard }"
          style="height: 100%;"
        >
          Log out
        </a>
      </div>
    </template>
    <a v-else href="/login" class="button-login">Log in</a>
  </div>
</template>

<script>
import axios from 'axios';
import swal from 'sweetalert';

export default {
  data() {
    // Check localStorage immediately to show cached state
    const isLoggedIn = localStorage.getItem('isLoggedIn') === '1';
    const cachedUser = localStorage.getItem('currentUser');

    let initialUser = { loggedIn: isLoggedIn };
    if (isLoggedIn && cachedUser) {
      try {
        initialUser = JSON.parse(cachedUser);
        initialUser.loggedIn = true;
      } catch (e) {
        // If parsing fails, fall back to simple logged in state
        initialUser = { loggedIn: true };
      }
    }

    return {
      gotResponse: false,
      currentUser: initialUser,
      menuOpen: false
    };
  },
  computed: {
    /** Patients only: name + dropdown (isOrdinaryUser from /api/v1/users/current). */
    showPatientLoginMenu() {
      return this.currentUser.loggedIn && this.currentUser.isOrdinaryUser === true;
    },
    isDashboard() {
      return window.location.pathname.startsWith('/dashboard');
    },
    displayName() {
      const u = this.currentUser;
      if (!u || !u.loggedIn) {
        return '';
      }
      if (u.label && String(u.label).trim()) {
        return String(u.label).trim();
      }
      const parts = [u.firstName, u.lastName].filter(Boolean);
      if (parts.length) {
        return parts.join(' ');
      }
      return 'Account';
    }
  },
  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    onDocumentClick() {
      if (this.menuOpen) {
        this.menuOpen = false;
      }
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.menuOpen) {
        this.menuOpen = false;
      }
    },
    startIdleTimer() {
      // Auto-logout Timer
      let minutesIdle = 0;
      const self = this;

      // Reset idle counter on user activity
      const resetIdle = () => {
        minutesIdle = 0;
      };

      // Remove existing listeners if any
      if (this.idleResetHandlers) {
        document.body.removeEventListener('mousemove', this.idleResetHandlers.mousemove);
        document.body.removeEventListener('keyup', this.idleResetHandlers.keyup);
      }

      // Store handlers for cleanup
      this.idleResetHandlers = {
        mousemove: resetIdle,
        keyup: resetIdle
      };

      document.body.addEventListener('mousemove', resetIdle);
      document.body.addEventListener('keyup', resetIdle);

      const idleChecker = () => {
        minutesIdle += 1;

        if (minutesIdle >= 5) {
          self.showLogoutPopup();
        }
      };

      const idleTimer = setInterval(idleChecker, 840000);
      this.idleTimer = idleTimer;
    },
    showLogoutPopup() {
      const self = this;
      const logoutTimeout = setTimeout(() => {
        window.location.replace('/logout');
      }, 60000);

      if (this.idleTimer) {
        clearInterval(this.idleTimer);
      }

      swal("You will be logged out in 1 minute due to inactivity. Click the button below to remain logged in", {
        buttons: {
          confirm: "Stay Logged In"
        }
      }).then(() => {
        clearTimeout(logoutTimeout);
        self.startIdleTimer();
      });
    }
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    axios.get(`/api/v1/users/current`)
      .then((response) => {
        this.gotResponse = true;
        if (response.data && response.data !== false) {
          this.currentUser = response.data.data;
          this.currentUser.loggedIn = true;
          this.startIdleTimer();
          const dashboardLink = document.querySelector('.dashboard-link');
          if (dashboardLink) {
            dashboardLink.style.setProperty('display', 'block');
          }
          localStorage.setItem('isLoggedIn', '1');
          // Cache the user data for faster initial render on next page load
          localStorage.setItem('currentUser', JSON.stringify(response.data.data));
        } else {
          // User is not logged in, update state
          this.currentUser = { loggedIn: false };
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('currentUser');
        }
      })
      .catch((error) => {
        this.gotResponse = true;
        // On error, assume not logged in
        this.currentUser = { loggedIn: false };
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('currentUser');
      });
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.idleTimer) {
      clearInterval(this.idleTimer);
    }
    if (this.idleResetHandlers) {
      document.body.removeEventListener('mousemove', this.idleResetHandlers.mousemove);
      document.body.removeEventListener('keyup', this.idleResetHandlers.keyup);
    }
  }
};
</script>

<style>
/* Hide login-buttons on mobile view unless inside .menu-active */
@media (max-width: 1100px) {
  .login-buttons {
      display: none;
  }

  .menu-active .login-buttons {
      display: block;
  }
}

/* Staff login: dashboard link — icon hidden by default, text visible */
.button-login--dashboard .dashboard-icon {
  display: none;
}

@media (min-width: 1100px) and (max-width: 1600px) {
  .button-login--dashboard .dashboard-icon {
    display: inline-block;
  }
  .button-login--dashboard .dashboard-text {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}
</style>

<style scoped>
.login-cta-dropdown {
  position: relative;
  height: 100%;
  display: flex;
  align-items: stretch;
}

.login-cta-dropdown__trigger {
  display: inline-flex !important;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  max-width: 16rem;
}

.login-cta-dropdown__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.login-cta-dropdown__caret {
  flex-shrink: 0;
  font-size: 0.65em;
  opacity: 0.85;
  transition: transform 0.2s ease;
}

.login-cta-dropdown__caret--open {
  transform: rotate(180deg);
}

.login-cta-dropdown__panel {
  position: absolute;
  right: 0;
  top: calc(100% + 0.35rem);
  min-width: 12rem;
  padding: 0.5rem 0;
  border-radius: 0.625rem;
  background-color: rgba(171, 171, 171, 0.35);
  -webkit-backdrop-filter: blur(15px);
  backdrop-filter: blur(15px);
  z-index: 1002;
  box-shadow: 0 4px 24px rgba(0, 38, 63, 0.12);
}

.login-cta-dropdown__link {
  display: block;
  padding: 0.65rem 1.15rem;
  color: rgba(0, 38, 63, 1) !important;
  text-decoration: none !important;
  font-size: 0.95rem;
  font-weight: 500;
}

.login-cta-dropdown__link:hover {
  background-color: rgba(255, 255, 255, 0.45);
}

@media (max-width: 1100px) {
  .login-cta-dropdown {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .login-cta-dropdown__trigger {
    max-width: none;
    width: 100%;
    justify-content: space-between;
  }

  .login-cta-dropdown__panel {
    position: static;
    margin-top: 0.35rem;
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/LoginCta.vue